import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import { Users, UserSearch, Building, MapPin, Calendar, Phone, FileText, Car, Loader2, AlertCircle, UserPlus } from "lucide-react";
import { useState, useEffect } from "react";
import { useAuth } from "../../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getLibroVisitas, LibroVisita, GetLibroVisitasRequest } from "../../../../api/libroVisitasService";
import { Button } from "../../../../components/ui/button";
import Swal from "sweetalert2";
import SearchVisitorModal from "./SearchVisitorModal";
import SearchResidentModal from "./SearchResidentModal";

export default function VisitorsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();
  const unidadId = session?.unidad?.id || "";
  
  const [visitors, setVisitors] = useState<LibroVisita[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedVisitor, setSelectedVisitor] = useState<LibroVisita | null>(null);
  const [showVisitors, setShowVisitors] = useState(false);
  const [imageErrors, setImageErrors] = useState<Set<number>>(new Set());
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [showSearchVisitorModal, setShowSearchVisitorModal] = useState(false);
  const [showSearchResidentModal, setShowSearchResidentModal] = useState(false);

  const getImageUrl = (rutaFoto: string) => {
    if (!rutaFoto || rutaFoto === "no_hay_imagen.png") {
      return null;
    }
    if (rutaFoto.startsWith('http://') || rutaFoto.startsWith('https://')) {
      return rutaFoto;
    }
    const apiBaseUrl = process.env.REACT_APP_API_PROD || 'https://api.easyph.com.co/api/';
    const baseUrl = apiBaseUrl.replace(/\/api\/?$/, '');
    return `${baseUrl}/${rutaFoto}`;
  };

  const fetchVisitors = async () => {
    if (!unidadId) {
      setError("No se encontró el código de la unidad residencial.");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const payload: GetLibroVisitasRequest = {
        "unidad residencial codigo": unidadId,
        "codigo": "",
        "nombre visitante": ""
      };
      
      const data = await getLibroVisitas(payload);
      setVisitors(data);
      
      if (data.length === 0) {
        Swal.fire({
          icon: "info",
          title: "Sin datos",
          text: "No se encontraron visitantes para esta unidad.",
          confirmButtonColor: "#1a3c34",
        });
      }
    } catch (err) {
      const errorMessage = "Error al cargar los visitantes: " + (err as Error).message;
      setError(errorMessage);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: errorMessage,
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleShowVisitors = () => {
    setShowVisitors(true);
    if (visitors.length === 0) {
      fetchVisitors();
    }
  };

  const handleSearchVisitorInvited = () => {
    setShowSearchVisitorModal(true);
  };

  const handleSearchResident = () => {
    setShowSearchResidentModal(true);
  };

  const handleCloseSearchVisitorModal = (success?: boolean) => {
    setShowSearchVisitorModal(false);
    if (success) {
      setShowVisitors(true);
      fetchVisitors();
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "No registrada";
    try {
      const date = new Date(dateString);
      return date.toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  const handleImageError = (codigo: number) => {
    setImageErrors(prev => new Set(prev).add(codigo));
  };

  const fetchVisitorDetail = async (codigo: number) => {
    if (!unidadId) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se encontró el código de la unidad residencial.",
        confirmButtonColor: "#1a3c34",
      });
      return;
    }

    setIsLoadingDetail(true);
    try {
      const payload: GetLibroVisitasRequest = {
        "unidad residencial codigo": unidadId,
        "codigo": codigo.toString(),
        "nombre visitante": ""
      };
      
      const data = await getLibroVisitas(payload);
      
      if (data && data.length > 0) {
        setSelectedVisitor(data[0]);
      } else {
        Swal.fire({
          icon: "warning",
          title: "Sin datos",
          text: "No se encontró información detallada del visitante.",
          confirmButtonColor: "#1a3c34",
        });
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar el detalle del visitante: " + (err as Error).message,
        confirmButtonColor: "#1a3c34",
      });
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const openModal = async (visitor: LibroVisita) => {
    await fetchVisitorDetail(visitor.lvi_codigo);
  };

  const closeModal = () => {
    setSelectedVisitor(null);
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 text-gray-900 w-full overflow-x-hidden">
      {/* Sidebar */}
      <Sidebar />

      {/* Contenido principal */}
      <main className="flex-1 p-3 sm:p-4 md:p-6 md:ml-64 transition-all w-full max-w-full overflow-x-hidden">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 md:mb-6 text-military-600 break-words">Libro de Visitantes</h2>

        {/* Botones de búsqueda */}
        {!showVisitors && (
          <Card className="shadow-xl mb-3 sm:mb-4 md:mb-6 border-l-4 border-military-500 rounded-lg bg-white w-full">
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <CardTitle className="text-base sm:text-lg md:text-xl text-military-600 flex items-center gap-2">
                <UserSearch className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">Opciones de Búsqueda</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <Button
                  onClick={handleSearchResident}
                  className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition h-16 sm:h-20 flex flex-col items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <UserSearch className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span className="font-semibold text-xs sm:text-base md:text-lg">Buscar Residente</span>
                </Button>
                
                <Button
                  onClick={handleSearchVisitorInvited}
                  className="w-full bg-military-500 hover:bg-military-600 text-white rounded-lg transition h-16 sm:h-20 flex flex-col items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <UserSearch className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span className="font-semibold text-xs sm:text-base md:text-lg">Buscar Visitante</span>
                </Button>
                
                <Button
                  onClick={handleShowVisitors}
                  className="w-full sm:col-span-2 bg-green-500 hover:bg-green-600 text-white rounded-lg transition h-16 sm:h-20 flex flex-col items-center justify-center gap-2 text-sm sm:text-base"
                >
                  <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                  <span className="font-semibold text-xs sm:text-base md:text-lg">Lista de Visitantes</span>
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista de Visitantes */}
        {showVisitors && (
          <Card className="shadow-xl border-l-4 border-military-500 rounded-lg bg-white w-full">
            <CardHeader className="p-3 sm:p-4 md:p-6">
              <div className="flex flex-col gap-3 sm:gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                  <CardTitle className="text-base sm:text-lg md:text-xl text-military-600 flex items-center gap-2 w-full sm:w-auto">
                    <Users className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                    <span className="truncate">Lista de Visitantes</span>
                  </CardTitle>
                  <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
                    <Button
                      onClick={() => navigate("/visitantes/crear")}
                      className="bg-military-500 hover:bg-military-600 text-white w-full sm:w-auto text-sm sm:text-base px-3 sm:px-4 py-2 flex items-center justify-center gap-2"
                    >
                      <UserPlus className="h-4 w-4" />
                      Agregar Visitante
                    </Button>
                    <Button
                      onClick={() => {
                        setShowVisitors(false);
                        setVisitors([]);
                      }}
                      variant="outline"
                      className="border-gray-300 text-gray-700 hover:bg-gray-50 w-full sm:w-auto text-sm sm:text-base px-3 sm:px-4 py-2"
                    >
                      Volver
                    </Button>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 sm:p-4 md:p-6 pt-0 sm:pt-0 md:pt-0">
              {isLoading ? (
                <div className="flex justify-center py-6">
                  <Loader2 className="h-8 w-8 animate-spin text-military-500" />
                </div>
              ) : error ? (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-600 text-sm">{error}</p>
                </div>
              ) : visitors.length === 0 ? (
                <p className="text-gray-600 text-center py-4">No hay visitantes registrados.</p>
              ) : (
                <>
                  {/* Vista de Cards para móvil */}
                  <div className="block md:hidden space-y-3 sm:space-y-4">
                    {visitors.map((visitor) => {
                      const imageUrl = getImageUrl(visitor.lvi_ruta_foto);
                      const hasImageError = imageErrors.has(visitor.lvi_codigo);
                      
                      return (
                        <div
                          key={visitor.lvi_codigo}
                          onClick={() => openModal(visitor)}
                          className="bg-white border border-gray-200 rounded-lg p-3 sm:p-4 shadow-sm hover:shadow-md transition cursor-pointer w-full"
                        >
                          <div className="flex items-start gap-3 sm:gap-4">
                            <div className="flex-shrink-0">
                              {imageUrl && !hasImageError ? (
                                <img
                                  src={imageUrl}
                                  alt={visitor.lvi_nombre_completo}
                                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-2 border-military-500"
                                  onError={() => handleImageError(visitor.lvi_codigo)}
                                />
                              ) : (
                                <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                  <Users className="h-7 w-7 sm:h-8 sm:w-8 text-gray-400" />
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 className="font-bold text-base sm:text-lg text-gray-900 break-words">
                                {visitor.lvi_nombre_completo}
                              </h3>
                              <div className="mt-2 space-y-1 text-xs sm:text-sm text-gray-600">
                                <p className="flex items-start gap-1 break-words">
                                  <Building className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
                                  <span className="break-words">{visitor.lvi_torre_nombre} - {visitor.lvi_apartamento_nombre}</span>
                                </p>
                                <p className="flex items-start gap-1 break-words">
                                  <Calendar className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
                                  <span className="break-words">{formatDate(visitor.lvi_fecha_ingreso)}</span>
                                </p>
                                {visitor.lvi_vehiculo && visitor.lvi_vehiculo_placa && (
                                  <p className="flex items-start gap-1 break-words">
                                    <Car className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0 mt-0.5" />
                                    <span className="break-words">{visitor.lvi_vehiculo_placa}</span>
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Vista de Tabla para desktop */}
                  <div className="hidden md:block overflow-x-auto w-full">
                    <div className="inline-block min-w-full align-middle">
                      <div className="overflow-hidden border border-gray-200 rounded-lg">
                        <div className="overflow-x-auto">
                          <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Foto</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Nombre</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Documento</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Celular</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Ubicación</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Fecha Ingreso</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Vehículo</th>
                                <th className="px-3 py-2 md:px-4 md:py-3 text-left text-xs font-bold text-military-600 uppercase tracking-wider">Acciones</th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {visitors.map((visitor) => {
                                const imageUrl = getImageUrl(visitor.lvi_ruta_foto);
                                const hasImageError = imageErrors.has(visitor.lvi_codigo);
                                
                                return (
                                  <tr
                                    key={visitor.lvi_codigo}
                                    className="hover:bg-gray-50 transition"
                                  >
                                    <td className="px-3 py-2 md:px-4 md:py-3 whitespace-nowrap">
                                      {imageUrl && !hasImageError ? (
                                        <img
                                          src={imageUrl}
                                          alt={visitor.lvi_nombre_completo}
                                          className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-military-500"
                                          onError={() => handleImageError(visitor.lvi_codigo)}
                                        />
                                      ) : (
                                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-gray-200 flex items-center justify-center border-2 border-military-500">
                                          <Users className="h-5 w-5 md:h-6 md:w-6 text-gray-400" />
                                        </div>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3">
                                      <div className="text-xs md:text-sm font-medium text-gray-900 break-words max-w-xs">{visitor.lvi_nombre_completo}</div>
                                      <div className="text-xs md:text-sm text-gray-500 break-words max-w-xs">{visitor.lvi_motivo_visita}</div>
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3">
                                      <div className="text-xs md:text-sm text-gray-900 break-words">{visitor.lvi_tipo_documento_nombre}</div>
                                      <div className="text-xs md:text-sm text-gray-500 break-words">{visitor.lvi_numero_documento}</div>
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words">
                                      {visitor.lvi_celular}
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3">
                                      <div className="text-xs md:text-sm text-gray-900 break-words">{visitor.lvi_torre_nombre}</div>
                                      <div className="text-xs md:text-sm text-gray-500 break-words">{visitor.lvi_apartamento_nombre}</div>
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm text-gray-900 break-words max-w-xs">
                                      {formatDate(visitor.lvi_fecha_ingreso)}
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3">
                                      {visitor.lvi_vehiculo && visitor.lvi_vehiculo_placa ? (
                                        <div className="text-xs md:text-sm text-gray-900 break-words">
                                          <Car className="h-3 w-3 md:h-4 md:w-4 inline mr-1" />
                                          {visitor.lvi_vehiculo_placa}
                                        </div>
                                      ) : (
                                        <span className="text-xs md:text-sm text-gray-400">Sin vehículo</span>
                                      )}
                                    </td>
                                    <td className="px-3 py-2 md:px-4 md:py-3 text-xs md:text-sm font-medium">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => openModal(visitor)}
                                        className="border-military-500 text-military-500 hover:bg-military-100 text-xs md:text-sm px-2 md:px-3 py-1 md:py-2"
                                      >
                                        Ver Detalle
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Modal de Detalle */}
        {(selectedVisitor || isLoadingDetail) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[95vh] overflow-y-auto">
              {isLoadingDetail ? (
                <div className="flex flex-col items-center justify-center p-8 sm:p-12">
                  <Loader2 className="h-12 w-12 animate-spin text-military-500 mb-4" />
                  <p className="text-gray-600 text-sm sm:text-base">Cargando detalle del visitante...</p>
                </div>
              ) : selectedVisitor ? (
                <>
                  {/* Header del Modal */}
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-4 sm:px-6 py-4 flex items-center justify-between z-10">
                    <h3 className="text-lg sm:text-xl font-bold text-military-600 flex items-center gap-2">
                      <Users className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="truncate">Detalle del Visitante</span>
                    </h3>
                    <button
                      onClick={closeModal}
                      className="text-gray-400 hover:text-gray-600 transition text-2xl font-bold leading-none"
                      aria-label="Cerrar"
                    >
                      ×
                    </button>
                  </div>
                  
                  {/* Contenido del Modal */}
                  <div className="p-4 sm:p-6 space-y-4 sm:space-y-5">
                    {/* 1. Foto visitante */}
                    <div className="flex justify-center">
                      {(() => {
                        const imageUrl = getImageUrl(selectedVisitor.lvi_ruta_foto);
                        const hasImageError = imageErrors.has(selectedVisitor.lvi_codigo);
                        
                        return imageUrl && !hasImageError ? (
                          <img
                            src={imageUrl}
                            alt={selectedVisitor.lvi_nombre_completo}
                            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full object-cover border-4 border-military-500 shadow-lg"
                            onError={() => handleImageError(selectedVisitor.lvi_codigo)}
                          />
                        ) : (
                          <div className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 rounded-full bg-gray-200 flex items-center justify-center border-4 border-military-500 shadow-lg">
                            <Users className="h-12 w-12 sm:h-16 sm:w-16 md:h-20 md:w-20 text-gray-400" />
                          </div>
                        );
                      })()}
                    </div>
                    
                    {/* Información del visitante */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                      {/* 2. Fecha de ingreso */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Fecha de Ingreso</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {formatDate(selectedVisitor.lvi_fecha_ingreso)}
                        </p>
                      </div>
                      
                      {/* 3. Nombre completo */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 sm:col-span-2">
                        <div className="flex items-start gap-2 mb-2">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Nombre Completo</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_nombre_completo}
                        </p>
                      </div>
                      
                      {/* 4. Torre */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Building className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Torre</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_torre_nombre || "No especificada"}
                        </p>
                      </div>
                      
                      {/* 5. Apartamento */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Building className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Apartamento</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_apartamento_nombre || "No especificado"}
                        </p>
                      </div>
                      
                      {/* 6. Tipo de documento */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Tipo de Documento</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_tipo_documento_nombre || "No especificado"}
                        </p>
                      </div>
                      
                      {/* 7. Número de documento */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Número de Documento</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_numero_documento || "No especificado"}
                        </p>
                      </div>
                      
                      {/* 8. Tipo de vehículo */}
                      {selectedVisitor.lvi_vehiculo && (
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                          <div className="flex items-start gap-2 mb-2">
                            <Car className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                            <p className="font-semibold text-xs sm:text-sm text-gray-700">Tipo de Vehículo</p>
                          </div>
                          <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                            {selectedVisitor.lvi_vehiculo || "No especificado"}
                          </p>
                        </div>
                      )}
                      
                      {/* 9. Placa */}
                      {selectedVisitor.lvi_vehiculo_placa && (
                        <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                          <div className="flex items-start gap-2 mb-2">
                            <Car className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                            <p className="font-semibold text-xs sm:text-sm text-gray-700">Placa</p>
                          </div>
                          <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                            {selectedVisitor.lvi_vehiculo_placa}
                          </p>
                        </div>
                      )}
                      
                      {/* 10. Número de teléfono */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200">
                        <div className="flex items-start gap-2 mb-2">
                          <Phone className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Número de Teléfono</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_celular || "No especificado"}
                        </p>
                      </div>
                      
                      {/* 12. Motivo de la visita */}
                      <div className="bg-gray-50 rounded-lg p-3 sm:p-4 border border-gray-200 sm:col-span-2">
                        <div className="flex items-start gap-2 mb-2">
                          <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-military-600 flex-shrink-0 mt-0.5" />
                          <p className="font-semibold text-xs sm:text-sm text-gray-700">Motivo de la Visita</p>
                        </div>
                        <p className="text-sm sm:text-base text-gray-900 pl-6 sm:pl-7 break-words">
                          {selectedVisitor.lvi_motivo_visita || "No especificado"}
                        </p>
                      </div>
                    </div>
                    
                    {/* Botón Cerrar */}
                    <Button
                      onClick={closeModal}
                      className="mt-4 sm:mt-6 w-full bg-military-500 hover:bg-military-600 text-white rounded-lg transition text-sm sm:text-base py-2 sm:py-3"
                    >
                      Cerrar
                    </Button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        )}

        {/* Modal de Búsqueda de Visitante Invitado */}
        <SearchVisitorModal
          isOpen={showSearchVisitorModal}
          onClose={handleCloseSearchVisitorModal}
        />

        {/* Modal de Búsqueda de Residente */}
        <SearchResidentModal
          isOpen={showSearchResidentModal}
          onClose={(success) => {
            setShowSearchResidentModal(false);
            if (success) {
              // Si se ingresó un residente, recargar la lista
              setShowVisitors(true);
              fetchVisitors();
            }
          }}
        />
      </main>
    </div>
  );
}

